import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { supabase } from '@/lib/supabase';
import { isGerman } from '@/i18n/locale';
import { Icon } from '@/components/ui/icons';

// ════════════════════════════════════════════════════════════════════
// SignupWall — anon → real account conversion
//
// Rendered inline in chat when an anonymous user has used their 3
// pre-signup messages. The mum stays in chat the whole time:
//
//   - "Continue with Google" → linkIdentity for the OAuth flow.
//     This preserves the user_id, so the conversation, baby, and any
//     other data attached to the anon user carry over to the linked
//     account with zero migration. After Google bounces back to
//     /auth/callback, the wall is dismissed and the chat is unblocked.
//
//   - "Continue with email" → updateUser({ email, password }).
//     Adds an email identity to the existing anon user. Same user_id.
//     Same data. The wall hides on success and the mum can resend
//     her last message (which was rejected by the server's 3-message
//     cap) and continue chatting.
//
// Edge case: email already belongs to a different account.
// updateUser fails with a clear error. We surface it and link the
// mum to /login with a hint to sign in instead. The anon thread is
// effectively abandoned (her data stays under the anon user_id; if
// she logs into the existing account, she lands on that account's
// data instead — by design, per the merge decision).
// ════════════════════════════════════════════════════════════════════

interface Props {
  babyName: string;
  onConverted?: () => void;
}

type Mode = 'idle' | 'email-open';

export function SignupWall({ babyName, onConverted }: Props) {
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const [mode, setMode] = useState<Mode>('idle');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline i18n — small enough that a string table for this one
  // component would be overkill. babyName is interpolated below.
  const t = useMemo(() => {
    if (isGerman()) {
      return {
        title: 'Möchtest du weitermachen?',
        bodyBaby: (n: string) => `Speichere ${n}s Verlauf und schalte 17 weitere Fragen diesen Monat frei.`,
        reassure: 'Keine Kreditkarte. Wir spammen dich nie.',
        google: 'Mit Google fortfahren',
        googleLoading: 'Google wird geöffnet…',
        emailCta: 'Mit E-Mail fortfahren',
        emailLabel: 'E-Mail',
        emailPlaceholder: 'du@beispiel.de',
        passwordLabel: 'Passwort',
        passwordPlaceholder: 'Mindestens 8 Zeichen',
        validationError: 'E-Mail und ein Passwort mit mindestens 8 Zeichen sind erforderlich.',
        save: 'Speichern & weiter',
        saving: 'Verlauf wird gespeichert…',
        back: 'Zurück',
        emailExistsPrefix: 'Diese E-Mail ist schon registriert. ',
        emailExistsLink: 'Stattdessen anmelden',
        emailExistsSuffix: ', um dieses Konto zu nutzen.',
      };
    }
    return {
      title: 'Want to keep going?',
      bodyBaby: (n: string) => `Save ${n}'s conversation and unlock 17 more questions this month.`,
      reassure: "No card needed. We'll never spam you.",
      google: 'Continue with Google',
      googleLoading: 'Opening Google…',
      emailCta: 'Continue with email',
      emailLabel: 'Email',
      emailPlaceholder: 'you@example.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'At least 8 characters',
      validationError: 'Email and a password of at least 8 characters are required.',
      save: 'Save & continue',
      saving: 'Saving your conversation…',
      back: 'Back',
      emailExistsPrefix: 'This email is already registered. ',
      emailExistsLink: 'Sign in instead',
      emailExistsSuffix: ' to use that account.',
    };
  }, []);

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    // signInWithGoogle navigates the browser away to Google; this call
    // never resolves with success. We only see a return value if the
    // SDK errored before redirecting.
    const { error: err } = await signInWithGoogle();
    if (err) {
      setError(err);
      setBusy(false);
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || password.length < 8) {
      setError(t.validationError);
      return;
    }

    setBusy(true);
    setError(null);

    // updateUser converts the anon user to a real one. Supabase keeps
    // the same auth.users row (and therefore the same user_id), just
    // flips is_anonymous=false and adds the email + password identity.
    const { error: updErr } = await supabase.auth.updateUser({
      email: email.trim(),
      password,
    });

    if (updErr) {
      // Most common: "User with this email address has already been
      // registered" — handled with a sign-in nudge in the UI below.
      setError(updErr.message);
      setBusy(false);
      return;
    }

    // Conversion succeeded. The sync trigger on auth.users.is_anonymous
    // has already flipped public.profiles.is_anonymous to false. The
    // next /api/chat-stream call will use the 20-msg monthly cap.
    onConverted?.();
  }

  const emailAlreadyExists =
    error != null &&
    /already.*registered|already.*exists|in use/i.test(error);

  return (
    <div className="rounded-2xl border border-primary-200 bg-gradient-to-br from-primary-50/60 via-cream to-accent/5 shadow-sm p-6 sm:p-7">
      {/* ─── Heading ─────────────────────────────────────────────── */}
      <div className="flex items-start gap-3">
        <div
          className="shrink-0 w-10 h-10 rounded-xl bg-white border border-primary-100 flex items-center justify-center text-primary-700 shadow-sm"
          aria-hidden
        >
          <Icon.Coffee size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-xl font-semibold text-slate leading-snug">
            {t.title}
          </h3>
          <p className="mt-1.5 text-[15px] text-slate leading-relaxed">
            {t.bodyBaby(babyName)}
          </p>
          <p className="mt-1 text-[13px] text-slate-muted">
            {t.reassure}
          </p>
        </div>
      </div>

      {/* ─── Google button ───────────────────────────────────────── */}
      <button
        type="button"
        onClick={handleGoogle}
        disabled={busy}
        className="mt-5 w-full inline-flex items-center justify-center gap-2.5 rounded-xl bg-white border border-primary-200 text-slate font-medium text-[15px] py-3 hover:bg-cream hover:border-primary-300 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <GoogleLogo />
        {busy && mode === 'idle' ? t.googleLoading : t.google}
      </button>

      {/* ─── Email path ──────────────────────────────────────────── */}
      {mode === 'idle' ? (
        <button
          type="button"
          onClick={() => {
            setMode('email-open');
            setError(null);
          }}
          disabled={busy}
          className="mt-2.5 w-full inline-flex items-center justify-center rounded-xl bg-primary text-white font-medium text-[15px] py-3 hover:bg-primary-600 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t.emailCta}
        </button>
      ) : (
        <form onSubmit={handleEmailSubmit} className="mt-4 space-y-3">
          <div>
            <label
              htmlFor="wall-email"
              className="block text-[13px] font-medium text-slate mb-1.5"
            >
              {t.emailLabel}
            </label>
            <input
              id="wall-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              placeholder={t.emailPlaceholder}
              className="w-full rounded-xl border border-primary-100 bg-white px-4 py-2.5 text-[15px] text-slate placeholder:text-slate-muted/60 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition disabled:opacity-50"
            />
          </div>
          <div>
            <label
              htmlFor="wall-password"
              className="block text-[13px] font-medium text-slate mb-1.5"
            >
              {t.passwordLabel}
            </label>
            <input
              id="wall-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              placeholder={t.passwordPlaceholder}
              className="w-full rounded-xl border border-primary-100 bg-white px-4 py-2.5 text-[15px] text-slate placeholder:text-slate-muted/60 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition disabled:opacity-50"
            />
          </div>

          {error && (
            <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-[13px] text-danger-700 leading-relaxed" role="alert">
              {emailAlreadyExists ? (
                <>
                  {t.emailExistsPrefix}
                  <Link to="/login" className="font-semibold underline">
                    {t.emailExistsLink}
                  </Link>
                  {t.emailExistsSuffix}
                </>
              ) : (
                error
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-primary text-white font-medium text-[15px] py-3 hover:bg-primary-600 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? t.saving : t.save}
            {!busy && <Icon.ArrowRight size={16} />}
          </button>

          <button
            type="button"
            onClick={() => {
              setMode('idle');
              setError(null);
            }}
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1 min-h-[40px] text-[13px] text-slate-muted hover:text-slate transition-colors disabled:opacity-50"
          >
            <Icon.ArrowLeft size={14} />
            {t.back}
          </button>
        </form>
      )}
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.13-.85 2.08-1.81 2.72v2.26h2.93c1.71-1.58 2.7-3.91 2.7-6.62z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.93-2.26c-.81.54-1.85.86-3.03.86-2.33 0-4.31-1.57-5.01-3.68H.96v2.32C2.44 15.98 5.48 18 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.99 10.74A5.4 5.4 0 0 1 3.7 9c0-.6.1-1.19.29-1.74V4.94H.96A8.99 8.99 0 0 0 0 9c0 1.45.35 2.83.96 4.06l3.03-2.32z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0 5.48 0 2.44 2.02.96 4.94l3.03 2.32C4.69 5.15 6.67 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
